import { CommonModule, DatePipe } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

// Components
import { ClipboardModule } from 'ngx-clipboard';
import { AddressDataFormComponent } from './components/address-data-form/address-data-form.component';
import { AdvanceSimulatorFormComponent } from './components/advance-simulator-form/advance-simulator-form.component';
import { HousingDataFormComponent } from './components/housing-data-form/housing-data-form.component';
import { BeginReviewComponent } from './components/modals/begin-review/begin-review.component';
import { UploadFileComponent } from './components/modals/upload-file/upload-file.component';
import { AdvanceSimulatorComponent } from './containers/advance-simulator/advance-simulator.component';
import { AppraisalComponent } from './containers/appraisal/appraisal.component';
import { AppraisalSectionComponent } from './containers/dashboard/components/appraisal-section/appraisal-section.component';
import { DocumentationSectionComponent } from './containers/dashboard/components/documentation-section/documentation-section.component';
import { SignatureSectionComponent } from './containers/dashboard/components/signature-section/signature-section.component';
import { YourRequestSectionComponent } from './containers/dashboard/components/your-request-section/your-request-section.component';
import { DashboardComponent } from './containers/dashboard/dashboard.component';
import { DocumentComponent } from './containers/documentation/components/document/document.component';
import { DocumentationComponent } from './containers/documentation/documentation.component';
import { ErrorDetailComponent } from './containers/error/components/error-detail/error-detail.component';
import { ErrorComponent } from './containers/error/error.component';
import { AskStateComponent } from './containers/layout/components/ask-state/ask-state.component';
import { StatusBarComponent } from './containers/layout/components/status-bar/status-bar.component';
import { YourMortgageComponent } from './containers/layout/components/your-mortgage/your-mortgage.component';
import { LayoutComponent } from './containers/layout/layout.component';
import { ResultComponent } from './containers/result/result.component';
import { SignatureComponent } from './containers/signature/signature.component';

// Modules
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { MultiSelectModule } from 'primeng/multiselect';
import { TooltipModule } from 'primeng/tooltip';
import { PreSimulatorModule } from '../pre-simulator/pre-simulator.module';
import { VassCoreModule } from '../vass-core/vass-core.module';
import { MortgageRoutingModule } from './mortgage-routing.module';

// Services
import { BeginReviewService } from './services/begin-review/begin-review.service';
import { DashboardSectionService } from './services/dashboard-section/dashboard-section.service';
import { DocumentationService } from './services/documentation/documentation.service';
import { LayoutService } from './services/layout/layout.service';
import { MortgageService } from './services/mortgage/mortgage.service';
import { SimulatorCacheService } from './services/simulator-cache/simulator-cache.service';

// Directives
import { DatePickerModule } from 'primeng/datepicker';
import { SharedModule } from '../shared/shared.module';
import { LifeInsuranceComponent } from './components/modals/life-insurance/life-insurance.component';
import { AppraisalFormComponent } from './containers/appraisal/components/appraisal-form/appraisal-form.component';
import { AppraisalModalComponent } from './containers/appraisal/components/appraisal-modal/appraisal-modal.component';
import { AppraisalResultComponent } from './containers/appraisal/components/appraisal-result/appraisal-result.component';
import { ModalErrorTpvComponent } from './containers/appraisal/components/modal-error-tpv/modal-error-tpv.component';
import { SimpleNoteComponent } from './containers/appraisal/components/simple-note/simple-note.component';
import { RejectedMortgageComponent } from './containers/dashboard/components/rejected-mortgage/rejected-mortgage.component';
import { LoginSecondHolderComponent } from './containers/login-second-holder/login-second-holder.component';
import { ConfirmDataSecHolderModalComponent } from './containers/summary-second-holder/components/confirm-data-sec-holder-modal/confirm-data-sec-holder-modal.component';
import { IncorrectDataModalComponent } from './containers/summary-second-holder/components/incorrect-data-modal/incorrect-data-modal.component';
import { SummarySecondHolderComponent } from './containers/summary-second-holder/summary-second-holder.component';
import { WallContainerComponent } from './containers/wall-container/wall-container.component';
import { AppraisalService } from './services/appraisal/appraisal.service';
import { AskStateService } from './services/ask-state/ask-state.service';
import { SignatureService } from './services/signature/signature.service';
import { SummarySecondHolderService } from './services/summary-second-holder/summary-second-holder.service';
import { WallService } from './services/wall/wall.service';

import { DeviceDetectorService } from 'ngx-device-detector';

@NgModule({
  declarations: [
    AdvanceSimulatorFormComponent,
    AdvanceSimulatorComponent,
    ResultComponent,
    HousingDataFormComponent,
    LayoutComponent,
    AddressDataFormComponent,
    DashboardComponent,
    DocumentationComponent,
    SignatureComponent,
    AppraisalComponent,
    ErrorComponent,
    UploadFileComponent,
    DocumentComponent,
    YourMortgageComponent,
    StatusBarComponent,
    ErrorDetailComponent,
    AskStateComponent,
    BeginReviewComponent,
    DocumentationSectionComponent,
    YourRequestSectionComponent,
    AppraisalSectionComponent,
    YourRequestSectionComponent,
    SignatureSectionComponent,
    SimpleNoteComponent,
    AppraisalFormComponent,
    AppraisalModalComponent,
    AppraisalResultComponent,
    WallContainerComponent,
    SummarySecondHolderComponent,
    IncorrectDataModalComponent,
    ConfirmDataSecHolderModalComponent,
    LoginSecondHolderComponent,
    LifeInsuranceComponent,
    RejectedMortgageComponent,
    ModalErrorTpvComponent,
  ],
  imports: [
    ClipboardModule,
    CommonModule,
    VassCoreModule,
    SharedModule,
    MortgageRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    TooltipModule,
    TranslateModule,
    PreSimulatorModule,
    MultiSelectModule,
    InfiniteScrollModule,
    DatePickerModule,
  ],
  exports: [],
  providers: [
    LayoutService,
    BeginReviewService,
    // LoginRegisterService,
    SimulatorCacheService,
    // UserDataService,
    DocumentationService,
    DashboardSectionService,
    AskStateService,
    AppraisalService,
    SignatureService,
    WallService,
    DatePipe,
    SummarySecondHolderService,
    DeviceDetectorService,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class MortgageModule {
  static forRoot(): ModuleWithProviders<MortgageModule> {
    return {
      ngModule: MortgageModule,
      providers: [MortgageService],
    };
  }
}
